<template>
    <form
        class="relay-form"
        @submit.prevent="createRelay"
    >
        <div
            class="form-group form-group-header relay-details-header"
            data-test-id="relay-details-header"
        >
            {{ relayType.label }} Details
        </div>
        <div
            v-if="!hideName"
            class="form-group"
        >
            <label
                for="site-name-textbox"
                data-test-id="site-name-title"
                >Name</label
            >
            <input
                id="site-name-textbox"
                v-model="site_name"
                data-test-id="site-name-textbox"
                maxlength="191"
                minlength="3"
                required
                type="text"
            />
        </div>

        <div
            v-if="isAdvancedRelay"
            class="form-group"
        >
            <slider-toggle-field
                label="SmartEdge Enabled"
                description="Turn this on if this Advanced Relay includes a SmartEdge device."
                :value="isSmartDeviceEnabled"
                :isLocked="forceSmartDevice"
                :disabled="forceSmartDevice"
                data-test-id="smartedge-enabled-toggle"
                @update="isSmartDeviceEnabled = $event"
            ></slider-toggle-field>
        </div>

        <div class="form-group">
            <label data-test-id="relay-tower-title">Mounted on Tower?</label>
            <div
                class="radio-options"
                data-test-id="radio-options"
            >
                <div class="radio-option">
                    <RadioButton
                        v-model="is_tower_mounted"
                        name="is_tower_mounted"
                        pt:input:required="true"
                        :value="true"
                        variant="filled"
                        inputId="relay-tower-yes"
                        data-test-id="relay-tower-yes"
                    />
                    <label
                        for="relay-tower-yes"
                        data-test-id="relay-tower-yes-text"
                        >Yes</label
                    >
                </div>
                <div class="radio-option">
                    <RadioButton
                        v-model="is_tower_mounted"
                        name="is_tower_mounted"
                        pt:input:required="true"
                        :value="false"
                        variant="filled"
                        inputId="relay-tower-no"
                        data-test-id="relay-tower-no"
                    />
                    <label
                        for="relay-tower-no"
                        data-test-id="relay-tower-no-text"
                        >No</label
                    >
                </div>
            </div>
        </div>
        <div
            v-if="!hideSerials"
            class="form-group"
        >
            <label
                for="customer-asset-textbox"
                data-test-id="customer-asset-title"
                >Customer Asset #</label
            >
            <input
                id="customer-asset-textbox"
                v-model="asset_number"
                data-test-id="customer-asset-textbox"
                maxlength="191"
                minlength="3"
                type="text"
            />
        </div>
        <div
            v-if="!hideSerials && (isEdgeRelay || isAdvancedRelay)"
            class="form-group"
        >
            <label
                for="relay–serial-number-textbox"
                data-test-id="relay–serial-number-title"
                >{{ relayType.label }} Serial #</label
            >
            <input
                id="relay–serial-number-textbox"
                v-model="relay_serial_number"
                data-test-id="relay–serial-number-textbox"
                required
                minlength="3"
                maxlength="191"
                type="text"
            />
        </div>

        <div class="form-group">
            <span class="label-with-tooltip">
                <label
                    for="relay–install-textbox"
                    data-test-id="relay–install-title"
                    >Install Date
                </label>
                <info-tooltip>Choose an installation date before today</info-tooltip>
            </span>
            <input
                id="relay–install-textbox"
                v-model="installedAt"
                data-test-id="relay–install-textbox"
                :max="maxInstalledAt"
                type="datetime-local"
                step="1"
                @focus="handleInstalledAtFocus"
            />
            <div class="help"><span class="label">Time Zone: </span><span>UTC</span></div>
        </div>
        <div class="form-group">
            <label
                for="relay–installer-textbox"
                data-test-id="relay–installer-title"
                >Installer Name</label
            >
            <input
                id="relay–installer-textbox"
                v-model="installed_by"
                data-test-id="relay–installer-textbox"
                maxlength="191"
                minlength="3"
                type="text"
            />
        </div>
        <div
            v-if="!hideLocation"
            class="form-group lat-long-container no-clearfix"
        >
            <div>
                <label
                    for="relay–latitude-textbox"
                    data-test-id="relay–latitude-title"
                    >Latitude</label
                >
                <input
                    id="relay–latitude-textbox"
                    v-model="latitude"
                    data-test-id="relay–latitude-textbox"
                    class="short"
                    type="number"
                    min="-90"
                    max="90"
                    step="any"
                />
            </div>
            <div>
                <label
                    for="relay–longitude-textbox"
                    data-test-id="relay–longitude-title"
                    >Longitude</label
                >
                <input
                    id="relay–longitude-textbox"
                    v-model="longitude"
                    data-test-id="relay–longitude-textbox"
                    class="short"
                    type="number"
                    min="-180"
                    max="180"
                    step="any"
                />
            </div>
        </div>
        <div
            v-if="!hideLocation"
            class="form-group no-clearfix"
        >
            <label
                for="relay–elevation-textbox"
                data-test-id="relay–elevation-title"
                >Elevation</label
            >
            <div>
                <input
                    id="relay–elevation-textbox"
                    v-model="altitude"
                    data-test-id="relay–elevation-textbox"
                    type="number"
                    step="any"
                />
                <div class="help">Meters</div>
            </div>
        </div>
        <div class="form-group">
            <label
                for="relay-description-textbox"
                data-test-id="relay-description-title"
                >Description</label
            >
            <textarea
                id="relay-description-textbox"
                v-model="description"
                class="short"
                data-test-id="relay-description-textbox"
                minlength="3"
                maxlength="500"
            />
        </div>
        <div
            v-if="isEdgeRelay || isAdvancedRelay"
            class="form-group form-group-header"
        >
            Power
        </div>
        <div
            class="form-group"
            data-test-id="relay-power-type-select"
        >
            <label
                for="relay-power-type-textbox"
                data-test-id="relay-power-type-title"
                >Type</label
            >
            <select
                id="relay-power-type-textbox"
                v-model="power_type"
                data-test-id="relay-power-type-textbox"
                required
            >
                <option
                    v-for="powerType in sortedRelayPowerTypes"
                    :key="powerType.type"
                    :value="`${powerType.type}`"
                    :data-test-id="`relay-power-type-select-${powerType.type.toLowerCase()}`"
                >
                    {{ powerType.label }}
                </option>
            </select>
        </div>
        <template v-if="power_type === relayPowerTypes.SOLAR.type">
            <div class="form-group">
                <label
                    for="relay-panel-textbox"
                    data-test-id="relay-panel-title"
                    >Panel</label
                >
                <input
                    id="relay-panel-textbox"
                    v-model="solar_power_panel_type"
                    data-test-id="relay-panel-textbox"
                    maxlength="50"
                    type="text"
                />
            </div>

            <div class="form-group">
                <label
                    for="relay-wattage-textbox"
                    data-test-id="relay-wattage-title"
                    >Wattage</label
                >
                <input
                    id="relay-wattage-textbox"
                    v-model="solar_power_wattage"
                    data-test-id="relay-wattage-textbox"
                    type="number"
                    class="hide-controls"
                    min=".0001"
                    max="4294967295"
                    step=".0001"
                />
                <div class="help">Max 4 decimal places</div>
            </div>
        </template>
        <template v-if="power_type === relayPowerTypes.AC.type">
            <div
                class="form-group"
                data-test-id="relay-power-source-field"
            >
                <label data-test-id="relay-power-source-title">Source</label>
                <div
                    class="radio-options"
                    data-test-id="relay-power-source-options"
                >
                    <div
                        v-for="powerSource in relayAcPowerSources"
                        :key="powerSource.type"
                        class="radio-option"
                    >
                        <RadioButton
                            v-model="ac_power_source_type"
                            name="ac_power_source_type"
                            :value="`${powerSource.type}`"
                            variant="filled"
                            :inputId="`relay-power-source-${powerSource.type}`"
                            :data-test-id="`relay-power-source-${powerSource.type}`"
                        />
                        <label
                            :for="`relay-power-source-${powerSource.type}`"
                            :data-test-id="`relay-power-source-${powerSource.type}=text`"
                            >{{ powerSource.label }}</label
                        >
                    </div>
                </div>
            </div>
            <div
                class="form-group"
                data-test-id="relay-voltage-field"
            >
                <label data-test-id="relay-voltage-title">Voltage</label>
                <div
                    class="radio-options"
                    data-test-id="relay-voltage-options"
                >
                    <div
                        v-for="powerVoltage in relayAcPowerVoltages"
                        :key="powerVoltage.type"
                        class="radio-option"
                    >
                        <RadioButton
                            v-model="ac_power_voltage"
                            name="ac_power_voltage"
                            :value="`${powerVoltage.type}`"
                            variant="filled"
                            :inputId="`relay-voltage-${powerVoltage.type}`"
                            :data-test-id="`relay-voltage-${powerVoltage.type}`"
                        />
                        <label
                            :for="`relay-voltage-${powerVoltage.type}`"
                            :data-test-id="`relay-voltage-${powerVoltage.type}=text`"
                            >{{ powerVoltage.label }}</label
                        >
                    </div>
                </div>
            </div>
            <div class="form-group">
                <label
                    for="relay-circuitamps-textbox"
                    data-test-id="relay-circuitamps-title"
                    >Circuit Amps</label
                >
                <input
                    id="relay-circuitamps-textbox"
                    v-model="ac_power_circuit_amps"
                    data-test-id="relay-circuitamps-textbox"
                    type="number"
                    class="hide-controls"
                    min=".0001"
                    max="4294967295"
                    step=".0001"
                />
                <div class="help">Max 4 decimal places</div>
            </div>
        </template>
        <template v-if="power_type === relayPowerTypes.BATTERY.type && isHastyRelay">
            <div class="form-group">
                <label data-test-id="relay-battery-is-external-title">Is Battery External?</label>
                <div
                    class="radio-options"
                    data-test-id="relay-battery-is-external-options"
                >
                    <div class="radio-option">
                        <RadioButton
                            v-model="is_battery_external"
                            name="is_battery_external"
                            :value="true"
                            variant="filled"
                            inputId="relay-battery-external-yes"
                            data-test-id="relay-battery-external-yes"
                        />
                        <label
                            for="relay-battery-external-yes"
                            data-test-id="relay-battery-external-yes-text"
                            >Yes</label
                        >
                    </div>
                    <div class="radio-option">
                        <RadioButton
                            v-model="is_battery_external"
                            name="is_battery_external"
                            :value="false"
                            variant="filled"
                            inputId="relay-battery-external-no"
                            data-test-id="relay-battery-external-no"
                        />
                        <label
                            for="relay-battery-external-no"
                            data-test-id="relay-battery-external-no-text"
                            >No</label
                        >
                    </div>
                </div>
            </div>
            <div
                v-if="is_battery_external"
                class="form-group"
            >
                <label
                    for="relay-battery-type-textbox"
                    data-test-id="relay-battery-type-title"
                    >Battery Type</label
                >
                <input
                    id="relay-battery-type-textbox"
                    v-model="battery_type"
                    data-test-id="relay-battery-type-textbox"
                    maxlength="50"
                    type="text"
                />
            </div>
        </template>
        <relay-radios
            v-if="!hideRadios"
            v-model="radios"
            :max-radios="maxRadios"
        />

        <div
            v-if="!hideImages"
            class="form-group"
        >
            <div
                class="form-group form-group-header"
                data-test-id="image-upload-title"
            >
                Image Upload
            </div>

            <div class="form-group form-group-wide">
                <RelayImageUpload
                    @file-removed="handleFileRemoved"
                    @file-uploaded="handleFileUploaded"
                    @uploading="handleUploading"
                    @uploading-complete="handleUploadingComplete"
                    @cover-image-set="handleCoverImageChanged"
                />
            </div>
        </div>
        <form-errors :response="errorResponse"></form-errors>

        <div
            v-if="!hideActions"
            class="form-group"
        >
            <button
                :class="['btn', 'btn-primary', { active: submitted && !imagesUploading }]"
                data-test-id="cta-save"
                type="submit"
            >
                <div>Create</div>
            </button>
        </div>
    </form>
</template>

<script>
import RadioButton from 'primevue/radiobutton'

import FormErrors from '@/components/FormErrors.vue'
import RelayImageUpload from '@/components/RelayImageUpload.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import relayTypes from '@/constants/relay-types'
import radioTypes from '@/constants/radio-types'
import relayPowerTypes, { ordered as sortedRelayPowerTypes } from '@/constants/relay-power-types'
import { ordered as relayAcPowerSources } from '@/constants/relay-ac-power-sources'
import { ordered as relayAcPowerVoltages } from '@/constants/relay-ac-power-voltages'
import InfoTooltip from '@/components/InfoTooltip.vue'
import powerFields from '@/mixins/powerFields'
import RelayRadios from '@/components/RelayRadios.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'

export default {
    components: {
        SliderToggleField,
        FormErrors,
        InfoTooltip,
        RadioButton,
        RelayImageUpload,
        RelayRadios,
    },

    mixins: [canToast, hasBackButton, needsOrganizationSlug, powerFields],

    props: {
        forceRelayType: {
            type: String,
            required: false,
            default: null,
        },
        forceSmartDevice: {
            type: Boolean,
            default: false,
        },
        hideImages: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideActions: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideRadios: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideLocation: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideName: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideSerials: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['form-updated'],

    data() {
        const relayType = this.forceRelayType ?? this.$route.query.type
        return {
            type: relayType,
            site_name: null,
            asset_number: null,
            relay_serial_number: null,
            installed_at: null,
            installed_by: null,
            latitude: null,
            longitude: null,
            altitude: null,
            description: null,
            radios: [],
            relayTypes,
            relayPowerTypes,
            sortedRelayPowerTypes,
            relayAcPowerSources,
            relayAcPowerVoltages,
            is_tower_mounted: null,
            errorResponse: null,
            submitted: false,
            images: [],
            imagesUploading: false,
            maxInstalledAt: null,
            coverImageUuid: null,
            radioTypes,
            isSmartDeviceEnabled: relayType === relayTypes.ADVANCED.type || this.forceSmartDevice,
        }
    },

    computed: {
        radiosForSubmission() {
            return this.radios.filter((radio) => !radio.ui_removed).map(({ urn, name }) => ({ urn, name }))
        },

        maxRadios() {
            return this.isHastyRelay ? 1 : 2
        },

        isEdgeRelay() {
            return this.type === relayTypes.EDGE.type
        },

        isHastyRelay() {
            return this.type === relayTypes.HASTY.type
        },

        isAdvancedRelay() {
            return this.type === relayTypes.ADVANCED.type
        },

        relayType() {
            return relayTypes[this.type]
        },

        /**
         * Sometimes, the HTML native datetime-local component can store
         * timestamps without seconds (HH:mm). This ensures that the
         * value we store always includes seconds (HH:mm:ss).
         */
        installedAt: {
            get() {
                return this.installed_at
            },
            set(value) {
                if (value) {
                    this.installed_at = window.moment(value).format('YYYY-MM-DDTHH:mm:ss')
                } else {
                    this.installed_at = null
                }
            },
        },
    },

    watch: {
        type(newValue) {
            if (newValue === relayTypes.HASTY.type) {
                this.relay_serial_number = null

                this.radios = this.radios.slice(0, 1)
            }
        },
        $data: {
            handler(newValue) {
                this.$emit('form-updated', newValue)
            },
            deep: true,
        },
    },

    mounted() {
        this.$emit('form-updated', this.$data)
    },

    methods: {
        handleInstalledAtFocus() {
            this.maxInstalledAt = window.moment().utc().format('YYYY-MM-DDTHH:mm:ss')
            if (this.installed_at === null) {
                this.installed_at = window.moment().startOf('day').hour(0).format('YYYY-MM-DDTHH:mm:ss')
            }
        },
        createRelay() {
            this.submitted = true

            this.$http
                .post(`/${this.organizationSlug}/relays`, {
                    type: this.type,
                    site_name: this.site_name,
                    asset_number: this.asset_number,
                    relay_serial_number: this.relay_serial_number,
                    installed_at: this.installed_at,
                    installed_by: this.installed_by,
                    latitude: this.latitude,
                    longitude: this.longitude,
                    altitude: this.altitude,
                    description: this.description,
                    is_tower_mounted: this.is_tower_mounted,
                    power_type: this.power_type,
                    solar_power_panel_type: this.solar_power_panel_type,
                    solar_power_wattage: this.solar_power_wattage,
                    ac_power_circuit_amps: this.ac_power_circuit_amps,
                    ac_power_source_type: this.ac_power_source_type,
                    ac_power_voltage: this.ac_power_voltage,
                    is_battery_external: this.is_battery_external,
                    battery_type: this.battery_type,
                    radios: this.radiosForSubmission,
                    images: this.images,
                    cover_image_uuid: this.coverImageUuid,
                    is_smart_device_enabled: this.isSmartDeviceEnabled,
                })
                .then(() => {
                    this.toast('Relay created!')
                    this.$router.push(`/${this.organizationSlug}/relays`)
                })
                .catch((error) => {
                    this.submitted = false
                    this.errorResponse = error.response
                })
        },
        handleFileRemoved(deletedId) {
            const index = this.images.findIndex((imageId) => imageId === deletedId)

            if (index > -1) {
                this.images.splice(index, 1)
            }
        },

        handleFileUploaded(uploadedId) {
            this.images.push(uploadedId)
        },

        handleUploading() {
            this.imagesUploading = true
        },

        handleUploadingComplete() {
            this.imagesUploading = false
        },

        handleCoverImageChanged(imageId) {
            this.coverImageUuid = imageId
        },
    },
}
</script>
<style scoped lang="scss">
.form-group-header {
    font-size: 15px;
    font-weight: 800;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    height: 24px;
}

.no-clearfix {
    &:after {
        display: none;
    }
}

.lat-long-container {
    display: flex;
    justify-content: space-between;
}

.form-group-wide {
    width: 545px;
}

.radio-options {
    display: flex;
    flex-direction: row;
    gap: 1.5rem;

    .radio-option {
        display: flex;
        gap: 0.5em;
        align-items: center;

        label {
            margin: 0;
        }
    }
}

input[type='number'].short {
    /* number doesn't respect width 100% */
    width: 190px;
}

input[type='number'] {
    /* number doesn't respect width 100% */
    width: 420px;
}

.label-with-tooltip {
    display: flex;
    align-items: flex-start;

    label {
        width: auto;
    }
}
</style>
